import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import Intercom, { shutdown as intercomShutdown } from '@intercom/messenger-js-sdk';
import config from '../../config';
import { getUserToken, getUserData } from '../../utils/permissions';
import { MOBILE_BREAKPOINT } from '../../hooks/useWindowWidth';

const INTERCOM_APP_ID = process.env.REACT_APP_INTERCOM_APP_ID || 'l3d0sdmx';

// Phones: the launcher sits bottom-LEFT so it never covers the chat send button
// (bottom-right on every composer). Intercom honours alignment on mobile web
// (only the paddings are ignored there), and the side is decided once per page
// load. "Phone" = the device's SHORTER screen side is under 768px AND its LONGER
// side still keeps the app in its mobile layout (<= MOBILE_BREAKPOINT, sidebar
// hidden), so the answer does not change when the phone is rotated, and a laptop
// or tablet whose screen reports a short side under 768 (Windows 150% scaling =
// 1280x720, macOS "Larger Text" = 1024x640, iPad mini = 744x1133) keeps the
// launcher bottom-right instead of over the sidebar's profile block. While the
// launcher is on the left the body carries LAUNCHER_LEFT_CLASS so a composer
// whose bottom-left holds something can make room (InsightEngineStyle
// InputBoxBottom: the character counter; ChatPanel.style ComposerShell: the
// typed text). Measured 2026-10-19 at 393px: the Insight Engine compact chat
// composer needs nothing.
const PHONE_SHORT_SIDE_PX = 768;
export const LAUNCHER_LEFT_CLASS = 'intercom-launcher-left';
const isPhone = () => {
  const { width, height } = window.screen || {};
  return Boolean(width && height)
    && Math.min(width, height) < PHONE_SHORT_SIDE_PX
    && Math.max(width, height) <= MOBILE_BREAKPOINT;
};
let launcherOnLeft = null; // null until the first boot of this page load

const shutdownLauncher = () => {
  intercomShutdown();
  document.body.classList.remove(LAUNCHER_LEFT_CLASS);
};

// Single source of truth for Intercom boot/shutdown.
//
// Reads user from localStorage (getUserToken + getUserData) — NOT from
// Redux. The reason: Login.js dispatches LOGIN_RESET right after
// LOGIN_SUCCESS, which wipes Redux back to initialState. Nothing
// re-hydrates Redux until a page refresh. The rest of the app survives
// because most components read from localStorage too. localStorage is
// written by LOGIN_SUCCESS *before* LOGIN_RESET fires, and only
// cleared by logoutApi(), so it's the only stable signal mid-session.
//
// Mounted INSIDE the Router (in AppLayout + NoSidebarLayout) so it can
// use useLocation. On every route change we re-fetch
// /my/intercom-enabled — that's what gives "on-the-fly" sync when an
// admin flips the org-level toggle.
//
// Decision tree (per effect run):
//   1. No localStorage token         → shutdown, no backend fetch
//   2. Token + userData present      → fetch flag → boot or shutdown
//
// Unmount cleanup calls shutdown so logout (navigation to /login,
// which has no layout) takes the bubble down cleanly.
const IntercomGate = () => {
  const location = useLocation();

  useEffect(() => {
    const token = getUserToken();
    const userData = getUserData();

    if (!token || !userData?.id) {
      shutdownLauncher();
      return;
    }

    let cancelled = false;
    axios
      .get(`${config.api.base_url}/organizations/my/intercom-enabled`, {
        headers: { Authorization: 'Bearer ' + token },
      })
      .then((res) => {
        if (cancelled) return;
        const enabled = res.data?.data?.intercom_enabled === true;
        if (enabled) {
          if (launcherOnLeft === null) {
            launcherOnLeft = isPhone();
          }
          Intercom({
            app_id: INTERCOM_APP_ID,
            user_id: userData.id,
            intercom_user_jwt: userData.intercom_user_jwt || undefined,
            ...(launcherOnLeft && { alignment: 'left' }),
          });
          document.body.classList.toggle(LAUNCHER_LEFT_CLASS, launcherOnLeft);
        } else {
          shutdownLauncher();
        }
      })
      .catch(() => {
        // Transient network failure — leave current state alone.
      });

    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  // Shutdown on unmount (covers logout, since /login has no layout and
  // therefore no IntercomGate mounted there).
  useEffect(() => {
    return () => shutdownLauncher();
  }, []);

  return null;
};

export default IntercomGate;
